import { useState, useCallback, useRef, useEffect } from "react";
import { useEnqueueSnackbar } from "../../../../hooks/useEnqueueSnackbar";
import apiClient from "../../../../auth/apiClient";

/**
 * Validates the alias string and returns an error message or null.
 * Validation priority: spaces → non-alphanumeric → length > 20
 */
export const validateAlias = (value) => {
  if (!value) return null;

  if (/\s/.test(value)) {
    return "Alias must not contain spaces";
  }

  if (!/^[a-zA-Z0-9]*$/.test(value)) {
    return "Alias must contain only letters and numbers";
  }

  if (value.length > 20) {
    return "Alias must be 20 characters or fewer";
  }

  return null;
};

const useOneParkAlias = (entityID, entityType, settingValues, disabled) => {
  const [alias, setAlias] = useState("");
  const [error, setError] = useState(null);
  const [isChecking, setIsChecking] = useState(false);
  const savedAliasRef = useRef("");

  const enqueueSnackbar = useEnqueueSnackbar();

  // Load existing alias from the facility alias API on mount
  useEffect(() => {
    if (!entityID) return;

    let cancelled = false;

    const loadAlias = async () => {
      try {
        const response = await apiClient.get(`/onepark/facility-aliases/${entityID}`);
        if (!cancelled && response.data?.entityAlias) {
          setAlias(response.data.entityAlias);
          savedAliasRef.current = response.data.entityAlias;
        }
      } catch (err) {
        // 404 means no alias set yet — that's fine
        if (err.response?.status !== 404 && !cancelled) {
          console.error("Failed to load facility alias:", err);
        }
      }
    };

    loadAlias();
    return () => { cancelled = true; };
  }, [entityID]);

  const handleChange = useCallback((e) => {
    const value = e.target.value;
    setAlias(value);

    const validationError = validateAlias(value);
    setError(validationError);
  }, []);

  const handleBlur = useCallback(async () => {
    if (disabled) return;

    const validationError = validateAlias(alias);
    if (validationError || !alias) return;

    if (alias === savedAliasRef.current) return;

    setIsChecking(true);

    try {
      // Directly call PUT — the backend handles uniqueness and returns 409 if duplicate
      const entityTypeValue = entityType?.toLowerCase() === "facilitygroup" ? 10 : 2;
      await apiClient.put("/onepark/facility-aliases", {
        entityId: entityID,
        entityAlias: alias,
        entityType: entityTypeValue,
      });

      savedAliasRef.current = alias;
    } catch (err) {
      if (err.response?.status === 409) {
        setError("This URL already exist, Please choose something unique");
      } else {
        enqueueSnackbar(
          "Unable to save the ONEpark alias. Please try again.",
          { variant: "error", tag: "OneParkAliasSaveFailed" }
        );
      }
    } finally {
      setIsChecking(false);
    }
  }, [alias, disabled, entityID, entityType, enqueueSnackbar]);

  return {
    alias,
    setAlias,
    error,
    handleChange,
    handleBlur,
    isChecking,
  };
};

export default useOneParkAlias;
